import type { ReactNode } from 'react';
import './PokerRulePaper.css';

type PokerCard = string;
const SUITS:Record<string,string>={S:'♠',H:'♥',D:'♦',C:'♣'};
const rank=(s:string)=>s.slice(0,-1)==='T'?'10':s.slice(0,-1);
function CardsPicture({cards,width=308,mark=[],label}:{cards:PokerCard[];width?:number;mark?:number[];label:string}) {
  const count=cards.length,cardW=40,gap=5,left=(width-(count*(cardW+gap)-gap))/2;
  return <svg viewBox={`0 0 ${width} 58`} style={{width}} role="img" aria-label={label} data-poker-cards={cards.join(' ')} data-card-width="40">
    {cards.map((c,i)=>{const x=left+i*(cardW+gap),suit=c.at(-1)!,red=suit==='H'||suit==='D';return <g key={`${c}-${i}`} data-card={c}>
      <rect x={x} y="1" width={cardW} height="55" rx="5" fill={mark.includes(i)?'#fff0ba':'#fffefa'} stroke={mark.includes(i)?'#a08b41':'#bbbba5'} strokeWidth={mark.includes(i)?'1.4':'.8'}/>
      <text x={x+6} y="22" fontSize="19" fontFamily="Georgia,serif" fill={red?'#b34248':'#30342e'}>{rank(c)}</text>
      <text x={x+26} y="46" textAnchor="middle" fontSize="24" fontFamily="Georgia,serif" fill={red?'#b34248':'#30342e'}>{SUITS[suit]}</text>
    </g>;})}
  </svg>;
}
function Panel({caption,children}:{caption:string;children:ReactNode}) {return <figure>{children}<figcaption>{caption}</figcaption></figure>;}
function ChipResult({mine,theirs,text}:{mine:number;theirs:number;text:string}) {return <div className="poker-chip-result" role="img" aria-label={`自分${mine}枚、相手${theirs}枚。${text}`}><span>自分 <b>{mine}</b>枚</span><span>相手 <b>{theirs}</b>枚</span><p>{text}</p></div>;}
function HandExample() {
  return <figure className="poker-hand-example" data-comparison="showdown-example">
    <p className="poker-card-heading">共有カード</p>
    <CardsPicture cards={['AD','7C','2S','9H','KH']} mark={[0]} label="共有カードはAダイヤ、7クラブ、2スペード、9ハート、Kハート。"/>
    <div className="poker-two-hands">
      <div><p>自分の手札</p><CardsPicture cards={['AS','AH']} width={148} mark={[0,1]} label="自分の手札はAスペードとAハート。共有カードのAと合わせてAのスリーカードです。"/><strong>Aのスリーカード</strong></div>
      <div><p>相手の手札</p><CardsPicture cards={['KC','QD']} width={148} label="相手の手札はKクラブとQダイヤ。共有カードのKと合わせてKのワンペアです。"/><span>Kのワンペア</span></div>
    </div>
    <figcaption>自分がポット38枚を受け取り、220枚対180枚で次のハンドへ進みます。</figcaption>
  </figure>;
}
const ranks:readonly [string,PokerCard[]][]=[
 ['ストレートフラッシュ',['AS','KS','QS','JS','TS']],
 ['フォーカード',['QS','QH','QD','QC','3S']],
 ['フルハウス',['TS','TH','TD','4S','4H']],
 ['フラッシュ',['AC','JC','8C','6C','2C']],
 ['ストレート',['8S','7H','6D','5C','4S']],
 ['スリーカード',['7S','7H','7D','KS','2C']],
 ['ツーペア',['AS','AH','9D','9C','4S']],
 ['ワンペア',['JS','JH','AD','7C','3S']],
 ['ハイカード',['AH','QH','9D','5C','2S']],
];
function RankAtlas() {return <div className="poker-rank-atlas" data-comparison="nine-hand-ranks">{ranks.map(([name,cards])=><figure key={name}><figcaption>{name}</figcaption><CardsPicture cards={cards} width={230} label={`${name}の例。${cards.map(c=>rank(c)+SUITS[c.at(-1)!]).join('、')}。`}/></figure>)}</div>;}
function CommunityStages() {
  const cards=['AD','7C','2S','9H','KH'];return <div className="poker-stages" data-comparison="community-stages">
    {[[0,'プリフロップ','SBから'],[3,'フロップ','BBから'],[4,'ターン','BBから'],[5,'リバー','BBから']].map(([n,name,first])=><figure key={name}><figcaption>{name} <span>{first}</span></figcaption><svg viewBox="0 0 308 58" style={{width:308}} role="img" aria-label={`${name}。共有カード${n}枚、${first}行動します。`} data-community-count={n} data-card-width="40">{Array.from({length:5},(_,i)=>{const x=44+i*45,c=cards[i],s=c.at(-1)!,red=s==='H'||s==='D';return <g key={i}><rect x={x} y="1" width="40" height="55" rx="5" fill={i<Number(n)?'#fffefa':'#eeeee2'} stroke="#bbbba5" strokeDasharray={i<Number(n)?undefined:'3 3'}/>{i<Number(n)&&<><text x={x+6} y="22" fontFamily="Georgia,serif" fontSize="19" fill={red?'#b34248':'#30342e'}>{rank(c)}</text><text x={x+26} y="46" textAnchor="middle" fontFamily="Georgia,serif" fontSize="24" fill={red?'#b34248':'#30342e'}>{SUITS[s]}</text></>}</g>;})}</svg></figure>)}
  </div>;
}
function BettingExample() {
  return <div className="poker-bet-comparison" data-comparison="sb-call-bb-option">
    <table><caption>最初のプリフロップ</caption><thead><tr><th></th><th>SB</th><th>BB</th></tr></thead><tbody><tr><th>賭けた合計</th><td>1枚</td><td>2枚</td></tr><tr><th>アンティ</th><td>0枚</td><td>2枚</td></tr></tbody></table>
    <table><caption>SBがコールした後</caption><thead><tr><th></th><th>SB</th><th>BB</th></tr></thead><tbody><tr><th>追加した額</th><td>1枚</td><td>0枚</td></tr><tr><th>賭けた合計</th><td>2枚</td><td>2枚</td></tr><tr><th>次の行動</th><td></td><td>BB</td></tr></tbody></table>
    <p>額がそろっても、まだBBの行動が残っています。</p>
  </div>;
}
function ShortCall() {
  return <div className="poker-short-call" data-comparison="short-call-refund">
    <table><caption>ポット300枚があり、相手が70枚をオールイン</caption><thead><tr><th></th><th>自分</th><th>相手</th></tr></thead><tbody><tr><th>残り</th><td>30枚</td><td>0枚</td></tr><tr><th>この段階の合計</th><td>0枚</td><td>70枚</td></tr></tbody></table>
    <table><caption>自分が残り30枚でコールした後</caption><thead><tr><th></th><th>自分</th><th>相手</th></tr></thead><tbody><tr><th>ポットへ入る額</th><td>30枚</td><td>30枚</td></tr><tr><th>残り</th><td>0枚</td><td>40枚</td></tr></tbody></table>
    <p>相手の超過分40枚が戻り、ポットは360枚になります。</p>
  </div>;
}
export function PokerRulePaper() {
  return <article className="rule-paper poker-paper">
    <div className="poker-primary" data-poker-primary>
      <section>
        <h2>ハンドを重ね、相手のチップをすべて取ると勝ちです。</h2>
        <p>2人とも200枚から始めます。1回の配りから決着までをハンドといいます。</p>
        <p>手札2枚と共有カード5枚から、最も強い5枚で役を作ります。手札は0枚・1枚・2枚のどの使い方でもかまいません。</p>
      </section>
      <section>
        <HandExample/>
        <p>相手がフォールドすれば、そのハンドは自分の勝ちです。最後まで残れば役を比べ、勝った側が両者の賭けたチップを受け取ります。</p>
      </section>
    </div>
    <div className="poker-details">
      <section data-poker-detail="ranks"><h3>役の強い順</h3><RankAtlas/>
        <p>Aが最も強く、K・Q・J・10…2と続きます。マークに強弱はありません。</p>
        <Panel caption="A・2・3・4・5は5が一番上のストレートです。"><CardsPicture cards={['AS','2H','3D','4C','5S']} width={230} label="Aスペード、2ハート、3ダイヤ、4クラブ、5スペード。Aを一番下として使う5ハイのストレートです。"/></Panel>
      </section>
      <section data-poker-detail="stages"><h3>共有カードは3枚、1枚、1枚</h3>
        <p>手札は自分だけが見られます。共有カードは両者が使い、それぞれの段階でチップを賭けます。</p><CommunityStages/>
        <p>プリフロップはSBから、その後はBBから行動します。両者の行動が終わり、その段階の賭けた合計がそろうと進みます。チェックが2回続いても進みます。</p>
      </section>
      <section data-poker-detail="actions"><h3>足りない額を合わせます。</h3>
        <dl className="poker-actions"><div><dt>チェック</dt><dd>合わせる額がないとき、追加せず番を渡します。</dd></div><div><dt>ベット</dt><dd>合わせる額がないとき、新しく賭けます。</dd></div><div><dt>コール</dt><dd>相手の合計まで、不足分を追加します。</dd></div><div><dt>レイズ</dt><dd>相手の合計より、さらに賭け額を増やします。</dd></div><div><dt>フォールド</dt><dd>ハンドを降ります。相手がポットを受け取ります。</dd></div></dl>
        <BettingExample/>
        <p>ベットするときはBB額以上を出します。レイズでは直前のベット額か、直前のレイズで増えた額以上を上乗せします。</p>
      </section>
      <section data-poker-detail="blinds"><h3>ブラインドとアンティ</h3>
        <p>各ハンドで、手札を配る前にチップを出します。ディーラーがSB、もう一人がBBです。ディーラーはハンドごとに交代します。</p>
        <p>最初はSBが1枚、BBが2枚を出します。BBはアンティ2枚もポットへ出します。ポットは両者が出したチップの集まりです。</p>
        <p>アンティは賭け額をそろえる計算に含めません。足りないときはBBがアンティを先に出します。</p>
        <p>BBは5ハンドごとに上がり、200枚で止まります。SBはBBの半分から端数を切り捨てた額を出します。アンティはBBと同じ額を出します。</p>
      </section>
      <section data-poker-detail="all-in"><h3>残り全部をオールイン</h3>
        <p>残りのチップを全部出すことをオールインといいます。最低額に届かなくてもできます。</p>
        <p>コールするチップが足りなければ、残り全部を出します。相手が出せなかった分はオールインした側へ戻します。</p><ShortCall/>
        <p>どちらかの残りが0枚になり、賭け額がそろったら、残りの共有カードをすべて出して役を比べます。</p>
      </section>
      <section data-poker-detail="compare"><h3>同じ役なら、カードの強さを比べます。</h3>
        <p>ペアや3枚・4枚の組は組の強さを先に比べます。フルハウスは3枚の組、2枚の組の順に比べます。ツーペアは強い方のペア、もう一方のペアの順に比べます。</p>
        <p>組が同じなら、残りのカードを強い順に比べます。ストレートは一番上、フラッシュとハイカードは強いカードから順に比べます。</p>
        <div className="poker-high-card" data-comparison="high-card-second-card"><Panel caption="Aが同じなら、次のKで勝ちます。"><CardsPicture cards={['AS','KH','9C','7S','4H']} width={230} mark={[1]} label="ハイカードA、K、9、7、4。相手も同じ共有9、7、4を使い、A、Q、9、7、4になります。2枚目のKが強く勝ちます。"/></Panel><Panel caption="Aの次はQです。"><CardsPicture cards={['AH','QD','9C','7S','4H']} width={230} label="前と同じ5枚の図とカード尺度。ハイカードA、Q、9、7、4。最高のAは同じですが、次のQがKに負けます。"/></Panel></div>
        <p>同じ強さならポットを半分ずつ分けます。割り切れない1枚はSBが受け取ります。</p>
        <Panel caption="共有カードだけで、両者が同じ役になることもあります。"><CardsPicture cards={['TS','JS','QS','KS','AS']} width={230} label="共有10、J、Q、K、Aのスペード5枚。両者とも手札を使わず同じストレートフラッシュになります。"/></Panel>
      </section>
      <section data-poker-detail="match-win"><h3>ハンドの勝ちを重ねます。</h3>
        <ChipResult mine={220} theirs={180} text="1ハンドに勝ち、次のハンドへ進みます。"/>
        <ChipResult mine={400} theirs={0} text="相手のチップが0枚になり、対戦の勝ちです。"/>
      </section>
    </div>
  </article>;
}
